/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { LinkListStyles } from "@insite/content-library/Widgets/Basic/LinkList";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import styled, { css } from "styled-components";

export interface ExtendedSiteMapLinksGridItem extends GridItemProps {
    linkListStyles?: LinkListStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: ExtendedSiteMapLinksGridItem;
    copyRightGridItem?: GridItemProps;
    copyRight?: any;
    subscribeSocialLinksGridItem?: any;
    generalLinksGridItem?: any;
}

export const footerContainerStyles: FooterContainerStyles = {
    widgetGridContainer: {
        gap: 0,
        css: css`
            margin: 0 auto;
            padding-left: 10px;
            padding-right: 10px;
        `,
    },
    linkList1GridItem: {
        width: [12, 6, 6, 3, 3],
    },
    linkList2GridItem: {
        width: [12, 6, 6, 2, 2],
    },
    linkList3GridItem: {
        width: [12, 6, 6, 3, 3],
    },
    linkList4GridItem: {
        width: [12, 6, 6, 4, 4],
    },
    subscribeGridItem: {
        width: [12, 12, 6, 4, 12],
    },
    socialLinksGridItem: {
        width: [12, 12, 6, 8, 12],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            order: -1;
                        `,
                        css`
                            order: -1;
                        `,
                    ],
                    "minMax",
                )}
        `,
    },
    siteMapLinksGridItem: {
        width: [12, 12, 7, 6, 6],
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            justify-content: center;
                            order: -1;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    copyRightGridItem: {
        width: [12, 12, 5, 6, 6],
        css: css`
            align-items: end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            justify-content: center;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    copyRight: {
        css: css`
            color: ${getColor("common.white")};
            font-size: 14px;
            line-height: 31px;
        `,
    },
    subscribeSocialLinksGridItem: {
        width: [12, 12, 12, 12, 4],
    },
    generalLinksGridItem: {
        width: [12, 12, 12, 12, 8],
    },
};

export const FooterContainerWrapper = styled.div``;

export const FooterTopSection = styled.div`
    background: ${getColor("common.ui3")};
    padding: 35px 0;
`;

export const FooterBottomSection = styled.div`
    background: ${getColor("text.main")};
    padding: 9px 0;
`;

export const FooterSiteMapLinksWrapper = styled.div`
    > div {
        > div {
            > div {
                > div {
                    &:after {
                        content: "|";
                        margin: 0 10px;
                        color: ${getColor("common.white")};
                    }

                    &:last-child {
                        &:after {
                            display: none;
                        }
                    }
                }
            }
        }
    }

    a {
        color: ${getColor("common.white")} !important;

        &,
        &:focus {
            outline: 0;
        }

        &:hover,
        &:focus {
            color: ${getColor("common.white")} !important;
            text-decoration-color: ${getColor("common.white")};
        }

        span {
            color: ${getColor("common.white")} !important;
            font-size: 14px;
            line-height: 31px;
            text-transform: none;
        }
    }

    > div > div {
        padding: 0;
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    css`
                        text-align: center;
                    `,
                ],
                "max",
            )}
        > div > div {
            padding-right: 0;
        }
    }
`;

export const GeneralLinksWrapper = styled.div`
    ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(
            theme,
            [
                null,
                null,
                null,
                css`
                    margin-bottom: 30px;
                `,
            ],
            "max",
        )}

    > div > div {
        padding: 0;

        > div {
            &:first-child {
                margin-bottom: 10px;
            }

            > div {
                padding: 0;
                margin-bottom: 6px;

                &:last-child {
                    margin-bottom: 0;
                }
            }

            a {
                color: ${getColor("text.main")} !important;
                text-decoration: none !important;

                span {
                    color: ${getColor("text.main")} !important;
                    font-size: 14px;
                    text-transform: none;
                }

                &,
                &:focus {
                    outline: 0;
                }

                &:hover,
                &:focus {
                    &,
                    span {
                        color: ${getColor("primary.main")} !important;
                    }
                }
            }
        }

        h3 {
            font-size: 18px;
            font-weight: 600;
        }
    }

    &.business-unit {
        width: 100%;

        > div {
            > div {
                &.link-list-horizontal {
                    > div {
                        &:after {
                            content: " ";
                            display: table;
                            clear: both;
                        }

                        > div {
                            width: 50%;
                            float: left;

                            ${({ theme }: { theme: BaseTheme }) =>
                                breakpointMediaQueries(
                                    theme,
                                    [
                                        null,
                                        css`
                                            width: 100%;
                                        `,
                                        null,
                                        null,
                                        null,
                                    ],
                                    "minMax",
                                )}
                        }
                    }
                }
            }
        }
    }

    &.support {
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    null,
                    css`
                        padding-left: 30px;
                    `,
                ],
                "min",
            )}
    }
`;

export const SubscribeSocialLinksWrapper = styled.div`
    width: 100%;
    ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(
            theme,
            [
                null,
                null,
                null,
                null,
                css`
                    padding-left: 58px;
                `,
            ],
            "min",
        )}
`;

export const SocialLinksWrapper = styled.div`
    width: 100%;
    > div {
        > div {
            &,
            * {
                padding: 0;
            }

            a {
                &,
                &:focus {
                    outline: 0;
                }
            }

            &:first-child {
                > * {
                    font-size: 14px;
                    font-weight: 600;
                    line-height: 26px;
                }
            }

            &:nth-child(2) {
                > div {
                    margin-right: 26px;
                    flex: 0 0 auto;

                    &:last-child {
                        margin-right: 0;
                    }

                    a {
                        display: flex;
                        width: 39px;
                        height: 39px;
                        border-radius: 100%;
                        background: transparent;
                        align-items: center;
                        justify-content: center;
                        transition: all 0.15s linear;

                        span {
                            transition: all 0.15s linear;
                            margin: 0;

                            svg {
                                &,
                                * {
                                    fill: ${getColor("primary.main")};
                                }
                            }
                        }

                        &:hover,
                        &:focus {
                            background: ${getColor("primary.main")};
                            color: ${getColor("primary.contrast")};

                            span {
                                color: ${getColor("primary.contrast")};

                                svg {
                                    &,
                                    * {
                                        fill: ${getColor("primary.contrast")};
                                        color: ${getColor("primary.main")};
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;
